import type { IncomingPostMessage, PostMessageType, PostMessageHandler } from '@/types/messages';
import type { WidgetConfig } from '@/types/config';
import { OriginValidator } from '@/security/OriginValidator';

/**
 * EventBus handles PostMessage communication between the widget and embedded iframes
 * with security validation via origin whitelisting
 */
export class EventBus {
  private listeners: Map<PostMessageType, Set<PostMessageHandler>> = new Map();
  private boundHandler: (event: MessageEvent) => void;
  private isListening = false;

  constructor(private config: WidgetConfig) {
    this.boundHandler = this.handleMessage.bind(this);
  }

  /**
   * Start listening to PostMessage events
   */
  public listen(): void {
    if (this.isListening) {
      console.warn('[CaredoveWidget] EventBus is already listening');
      return;
    }

    window.addEventListener('message', this.boundHandler);
    this.isListening = true;
  }

  /**
   * Stop listening to PostMessage events
   */
  public unlisten(): void {
    if (!this.isListening) {
      return;
    }

    window.removeEventListener('message', this.boundHandler);
    this.isListening = false;
  }

  /**
   * Handle incoming PostMessage events
   */
  private handleMessage(event: MessageEvent): void {
    // Security: Validate origin against whitelist
    if (!OriginValidator.isAllowed(event.origin, this.config.subdomain)) {
      console.warn(`[CaredoveWidget] Blocked message from untrusted origin: ${event.origin}`);
      return;
    }

    // Validate message structure
    if (!event.data || typeof event.data !== 'object') {
      return;
    }

    const message = event.data as Partial<IncomingPostMessage>;

    if (!message.message) {
      return;
    }

    // Dispatch to registered handlers
    const handlers = this.listeners.get(message.message);
    if (handlers && handlers.size > 0) {
      handlers.forEach(handler => {
        try {
          handler(message as IncomingPostMessage);
        } catch (error) {
          console.error('[CaredoveWidget] Error in message handler:', error);
        }
      });
    }
  }

  /**
   * Register a handler for a specific message type
   * @param messageType - Type of message to listen for
   * @param handler - Handler function to call when message is received
   */
  public on(messageType: PostMessageType, handler: PostMessageHandler): void {
    if (!this.listeners.has(messageType)) {
      this.listeners.set(messageType, new Set());
    }

    this.listeners.get(messageType)!.add(handler);
  }

  /**
   * Unregister a handler for a specific message type
   * @param messageType - Type of message
   * @param handler - Handler function to remove
   */
  public off(messageType: PostMessageType, handler: PostMessageHandler): void {
    const handlers = this.listeners.get(messageType);
    if (handlers) {
      handlers.delete(handler);

      // Clean up empty sets
      if (handlers.size === 0) {
        this.listeners.delete(messageType);
      }
    }
  }

  /**
   * Send a message to an iframe
   * @param iframe - Target iframe element
   * @param message - Message to send
   */
  public sendToIframe(iframe: HTMLIFrameElement, message: Record<string, unknown>): void {
    if (!iframe.contentWindow) {
      console.error('[CaredoveWidget] Cannot send message: iframe has no contentWindow');
      return;
    }

    try {
      // Get target origin from iframe src
      const iframeSrc = iframe.src;
      const url = new URL(iframeSrc);
      const targetOrigin = url.origin;

      // Validate target origin
      if (!OriginValidator.isAllowed(targetOrigin, this.config.subdomain)) {
        console.error('[CaredoveWidget] Cannot send message to untrusted origin:', targetOrigin);
        return;
      }

      // Send message with specific origin (not wildcard!)
      iframe.contentWindow.postMessage(message, targetOrigin);
    } catch (error) {
      console.error('[CaredoveWidget] Error sending message to iframe:', error);
    }
  }

  /**
   * Remove all listeners and cleanup
   */
  public destroy(): void {
    this.unlisten();
    this.listeners.clear();
  }

  /**
   * Get count of registered handlers for debugging
   */
  public getHandlerCount(): number {
    let count = 0;
    this.listeners.forEach(handlers => {
      count += handlers.size;
    });
    return count;
  }

  /**
   * Check if EventBus is currently listening
   */
  public get listening(): boolean {
    return this.isListening;
  }
}
